import { useState, useEffect } from "react";
import { supabase } from "./supabaseClient";

const NAVY_DARK = "#111D33";
const ACCENT = "#D20A11";
const BLUE = "#003A8F";

export default function CoachDashboard({ user, onLogout }) {
  const [inviteEmail, setInviteEmail] = useState("");
  const [inviteName, setInviteName] = useState("");
  const [loading, setLoading] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const [players, setPlayers] = useState([]);
  const [loadingPlayers, setLoadingPlayers] = useState(true);

  useEffect(() => { loadPlayers(); }, []);

  const loadPlayers = async () => {
    setLoadingPlayers(true);
    const { data, error } = await supabase.auth.admin?.listUsers?.() || { data: null };
    // Fallback: load from profiles table if available
    const { data: profiles } = await supabase.from("profiles").select("*").eq("role", "player");
    if (profiles) setPlayers(profiles);
    setLoadingPlayers(false);
  };

  const invitePlayer = async () => {
    if (!inviteEmail) { setError("Bitte E-Mail eingeben."); return; }
    setLoading(true);
    setError("");
    setMessage("");

    const { data, error } = await supabase.auth.signInWithOtp({
      email: inviteEmail,
      options: {
        emailRedirectTo: window.location.origin,
        data: { role: "player", name: inviteName },
        shouldCreateUser: true,
      }
    });

    if (error) {
      setError(`Fehler: ${error.message}`);
    } else {
      setMessage(`✓ Einladung an ${inviteEmail} gesendet!`);
      setInviteEmail("");
      setInviteName("");
      loadPlayers();
    }
    setLoading(false);
  };

  return (
    <div style={{ minHeight: "100vh", background: "#f4f6fa", fontFamily: "'Arial', sans-serif" }}>

      {/* Header */}
      <div style={{ background: NAVY_DARK, padding: "0" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", padding: "24px 28px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          <div>
            <div style={{ fontSize: 10, letterSpacing: 4, color: "rgba(255,255,255,0.4)", textTransform: "uppercase", marginBottom: 4 }}>FC Basel 1893</div>
            <div style={{ fontSize: 22, fontWeight: 900, color: "#fff", letterSpacing: 2, textTransform: "uppercase" }}>PRO-TRACK</div>
            <div style={{ fontSize: 10, color: ACCENT, letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginTop: 2 }}>Coach Dashboard</div>
          </div>
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,0.4)", marginBottom: 8 }}>{user.email}</div>
            <button onClick={onLogout} style={{
              background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.15)",
              color: "#fff", padding: "6px 14px", borderRadius: 6,
              fontSize: 10, cursor: "pointer", letterSpacing: 2, textTransform: "uppercase", fontWeight: 700,
            }}>Logout</button>
          </div>
        </div>
      </div>

      <div style={{ maxWidth: 900, margin: "0 auto", padding: "28px" }}>

        {/* Stats row */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 28 }}>
          {[
            { label: "Eingeladene Spieler", value: players.length || "—" },
            { label: "Aktive Sessions", value: "—" },
            { label: "Ø Completion Rate", value: "—" },
          ].map((s, i) => (
            <div key={i} style={{ background: "#fff", borderRadius: 10, padding: "20px 24px", borderLeft: `3px solid ${i === 0 ? ACCENT : BLUE}`, boxShadow: "0 2px 8px rgba(0,0,0,0.06)" }}>
              <div style={{ fontSize: 9, color: "rgba(0,0,0,0.35)", letterSpacing: 2, textTransform: "uppercase", fontWeight: 700, marginBottom: 8 }}>{s.label}</div>
              <div style={{ fontSize: 28, fontWeight: 900, color: NAVY_DARK }}>{s.value}</div>
            </div>
          ))}
        </div>

        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }}>

          {/* Invite Card */}
          <div style={{ background: "#fff", borderRadius: 12, padding: "28px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: NAVY_DARK, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Spieler einladen</div>
            <div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginBottom: 24 }}>Spieler erhält eine E-Mail mit Einladungslink zur App.</div>

            {message && (
              <div style={{ background: `${ACCENT}15`, border: `1px solid ${ACCENT}`, borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: NAVY_DARK, fontWeight: 700 }}>
                {message}
              </div>
            )}
            {error && (
              <div style={{ background: "#fff0f0", border: "1px solid #ffaaaa", borderRadius: 8, padding: "10px 14px", marginBottom: 16, fontSize: 12, color: "#cc0000" }}>
                {error}
              </div>
            )}

            <div style={{ marginBottom: 14 }}>
              <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, fontWeight: 700 }}>Name (optional)</div>
              <input
                type="text"
                value={inviteName}
                onChange={e => setInviteName(e.target.value)}
                placeholder="Vorname Nachname"
                style={{ width: "100%", padding: "11px 14px", borderRadius: 8, background: "#f4f6fa", border: "1px solid rgba(0,0,0,0.1)", fontSize: 13, outline: "none", boxSizing: "border-box", color: NAVY_DARK }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <div style={{ fontSize: 10, color: "rgba(0,0,0,0.4)", letterSpacing: 2, textTransform: "uppercase", marginBottom: 6, fontWeight: 700 }}>E-Mail</div>
              <input
                type="email"
                value={inviteEmail}
                onChange={e => setInviteEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && invitePlayer()}
                placeholder="spieler@email.com"
                style={{ width: "100%", padding: "11px 14px", borderRadius: 8, background: "#f4f6fa", border: "1px solid rgba(0,0,0,0.1)", fontSize: 13, outline: "none", boxSizing: "border-box", color: NAVY_DARK }}
              />
            </div>

            <button onClick={invitePlayer} disabled={loading} style={{
              width: "100%", padding: "13px",
              background: loading ? "rgba(210,10,17,0.5)" : ACCENT,
              border: "none", borderRadius: 8, color: "#fff",
              fontSize: 11, fontWeight: 900, cursor: loading ? "not-allowed" : "pointer",
              letterSpacing: 3, textTransform: "uppercase",
            }}>
              {loading ? "Wird gesendet..." : "Einladung senden →"}
            </button>
          </div>

          {/* Players list */}
          <div style={{ background: "#fff", borderRadius: 12, padding: "28px", boxShadow: "0 2px 12px rgba(0,0,0,0.06)" }}>
            <div style={{ fontSize: 14, fontWeight: 900, color: NAVY_DARK, marginBottom: 4, textTransform: "uppercase", letterSpacing: 1 }}>Spieler</div>
            <div style={{ fontSize: 11, color: "rgba(0,0,0,0.4)", marginBottom: 24 }}>Alle eingeladenen Spieler</div>

            {loadingPlayers ? (
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", textAlign: "center", padding: "20px 0" }}>Lädt...</div>
            ) : players.length === 0 ? (
              <div style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", textAlign: "center", padding: "20px 0", fontStyle: "italic" }}>
                Noch keine Spieler eingeladen.
              </div>
            ) : (
              players.map((p, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>
                  <div style={{ width: 32, height: 32, borderRadius: "50%", background: NAVY_DARK, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 12, fontWeight: 900, color: "#fff" }}>
                    {(p.name || p.email || "?")[0].toUpperCase()}
                  </div>
                  <div>
                    <div style={{ fontSize: 13, fontWeight: 700, color: NAVY_DARK }}>{p.name || "—"}</div>
                    <div style={{ fontSize: 10, color: "rgba(0,0,0,0.35)" }}>{p.email}</div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
